import { call, cancelled, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  CREATE_CHAT_DIRECT,
  GET_CHAT_CONVERSATIONS,
  GET_CHAT_MESSAGES,
  GET_CHAT_UNREAD,
  MARK_CHAT_CONVERSATION_READ,
  SEND_CHAT_MESSAGE
} from '../constants';
import axios from '../../utils/axios';
import { requestFail, requestPending, requestSuccess } from '../../utils/status';
import { chatScopeParams } from '../../helper/chat';
import { CHAT_MESSAGES_PAGE_SIZE } from '../../views/chat/constants';
import { getChatUnread } from '../actions/chat';

let conversationsRequestId = 0;
let messagesRequestId = 0;
let unreadRequestId = 0;

function* getChatConversationsSaga(action) {
  const {
    page = 1,
    building,
    org,
    q,
    replace = page <= 1,
    success: successPayload,
    fail: failPayload
  } = action.payload || {};

  const requestId = ++conversationsRequestId;
  const scope = chatScopeParams({ building, org, q });
  const requestMeta = {
    page: Number(page) || 1,
    replace: Boolean(replace),
    requestId,
    building: scope.building || 0,
    org: scope.org || 0,
    q: scope.q || ''
  };

  try {
    yield put({
      type: requestPending(GET_CHAT_CONVERSATIONS),
      payload: requestMeta
    });

    const query = new URLSearchParams({ page: String(requestMeta.page) });
    Object.entries(scope).forEach(([key, value]) => {
      query.set(key, String(value));
    });

    const idToken = localStorage.getItem('idToken');
    const res = yield call(axios.request, {
      url: `/chat/conversations?${query.toString()}`,
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      }
    });

    if (requestId !== conversationsRequestId) return;

    yield put({
      type: requestSuccess(GET_CHAT_CONVERSATIONS),
      payload: {
        ...(res.data || {}),
        _request: requestMeta
      }
    });

    if (successPayload) successPayload(res);
  } catch (err) {
    if (yield cancelled()) return;
    if (requestId !== conversationsRequestId) return;

    const errRes = err?.response || err;
    yield put({
      type: requestFail(GET_CHAT_CONVERSATIONS),
      payload: {
        ...(errRes || {}),
        _request: requestMeta
      }
    });
    if (failPayload) failPayload(errRes);
  }
}

function* getChatUnreadSaga(action) {
  const {
    building,
    org,
    success: successPayload,
    fail: failPayload
  } = action.payload || {};
  const requestId = ++unreadRequestId;
  const scope = chatScopeParams({ building, org });
  const requestMeta = {
    requestId,
    building: scope.building || 0,
    org: scope.org || 0
  };

  try {
    yield put({
      type: requestPending(GET_CHAT_UNREAD),
      payload: requestMeta
    });

    const query = new URLSearchParams(scope).toString();
    const url = query ? `/chat/unread?${query}` : '/chat/unread';
    const idToken = localStorage.getItem('idToken');
    const res = yield call(axios.request, {
      url,
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      }
    });

    if (requestId !== unreadRequestId) return;

    yield put({
      type: requestSuccess(GET_CHAT_UNREAD),
      payload: {
        ...(res.data || {}),
        _request: requestMeta
      }
    });
    if (successPayload) successPayload(res);
  } catch (err) {
    if (yield cancelled()) return;
    if (requestId !== unreadRequestId) return;
    const errRes = err?.response || err;
    yield put({
      type: requestFail(GET_CHAT_UNREAD),
      payload: {
        ...(errRes || {}),
        _request: requestMeta
      }
    });
    if (failPayload) failPayload(errRes);
  }
}

function* getChatMessagesSaga(action) {
  const {
    id,
    before_id = 0,
    limit,
    success: successPayload,
    fail: failPayload
  } = action.payload || {};
  const conversationId = Number(id) || 0;
  const beforeId = Number(before_id) || 0;
  const pageSize = limit || CHAT_MESSAGES_PAGE_SIZE;
  const requestId = ++messagesRequestId;
  const requestMeta = { conversationId, beforeId, requestId };

  try {
    yield put({
      type: requestPending(GET_CHAT_MESSAGES),
      payload: requestMeta
    });

    let url = `/chat/conversations/${conversationId}/messages?limit=${pageSize}`;
    if (beforeId) {
      url += `&before_id=${beforeId}`;
    }

    const idToken = localStorage.getItem('idToken');
    const res = yield call(axios.request, {
      url,
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      }
    });

    if (requestId !== messagesRequestId) return;

    yield put({
      type: requestSuccess(GET_CHAT_MESSAGES),
      payload: {
        ...(res.data || {}),
        _request: requestMeta
      }
    });
    if (successPayload) successPayload(res);
  } catch (err) {
    if (yield cancelled()) return;
    if (requestId !== messagesRequestId) return;

    const errRes = err?.response || err;
    yield put({
      type: requestFail(GET_CHAT_MESSAGES),
      payload: {
        ...(errRes || {}),
        _request: requestMeta
      }
    });
    if (failPayload) failPayload(errRes);
  }
}

function* markChatConversationReadSaga(action) {
  const {
    id,
    body,
    success: successPayload,
    fail: failPayload
  } = action.payload || {};
  const conversationId = Number(id) || 0;
  const requestMeta = { conversationId };

  try {
    yield put({
      type: requestPending(MARK_CHAT_CONVERSATION_READ),
      payload: requestMeta
    });

    const idToken = localStorage.getItem('idToken');
    const res = yield call(axios.request, {
      url: `/chat/conversations/${conversationId}/read`,
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      data: body
    });

    yield put({
      type: requestSuccess(MARK_CHAT_CONVERSATION_READ),
      payload: {
        ...(res.data || {}),
        _request: requestMeta
      }
    });

    // Refresh scoped badge; do not infer hasUnread from the loaded inbox page alone.
    const listScope = yield select((state) => state.chat?.listScope) || {};
    yield put(
      getChatUnread(
        chatScopeParams({
          building: listScope.building,
          org: listScope.org
        })
      )
    );

    if (successPayload) successPayload(res);
  } catch (err) {
    if (yield cancelled()) return;
    const errRes = err?.response || err;
    yield put({
      type: requestFail(MARK_CHAT_CONVERSATION_READ),
      payload: {
        ...(errRes || {}),
        _request: requestMeta
      }
    });
    if (failPayload) failPayload(errRes);
  }
}

function* sendChatMessageSaga(action) {
  const {
    id,
    body,
    success: successPayload,
    fail: failPayload
  } = action.payload || {};
  const conversationId = Number(id) || 0;
  const requestMeta = { conversationId };

  try {
    yield put({
      type: requestPending(SEND_CHAT_MESSAGE),
      payload: requestMeta
    });

    const idToken = localStorage.getItem('idToken');
    const res = yield call(axios.request, {
      url: `/chat/conversations/${conversationId}/messages`,
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      data: body
    });

    yield put({
      type: requestSuccess(SEND_CHAT_MESSAGE),
      payload: {
        ...(res.data || {}),
        _request: requestMeta
      }
    });
    if (successPayload) successPayload(res);
  } catch (err) {
    if (yield cancelled()) return;
    const errRes = err?.response || err;
    yield put({
      type: requestFail(SEND_CHAT_MESSAGE),
      payload: {
        ...(errRes || {}),
        _request: requestMeta
      }
    });
    if (failPayload) failPayload(errRes);
  }
}

function* createChatDirectSaga(action) {
  const {
    body,
    success: successPayload,
    fail: failPayload
  } = action.payload || {};

  try {
    yield put({ type: requestPending(CREATE_CHAT_DIRECT) });

    const idToken = localStorage.getItem('idToken');
    const res = yield call(axios.request, {
      url: '/chat/directs',
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      data: body
    });

    yield put({
      type: requestSuccess(CREATE_CHAT_DIRECT),
      payload: res.data || {}
    });
    if (successPayload) successPayload(res);
  } catch (err) {
    if (yield cancelled()) return;
    const errRes = err?.response || err;
    yield put({
      type: requestFail(CREATE_CHAT_DIRECT),
      payload: errRes
    });
    if (failPayload) failPayload(errRes);
  }
}

export default function* chatSaga() {
  yield takeLatest(GET_CHAT_CONVERSATIONS, getChatConversationsSaga);
  yield takeLatest(GET_CHAT_UNREAD, getChatUnreadSaga);
  yield takeLatest(GET_CHAT_MESSAGES, getChatMessagesSaga);
  // takeEvery so switching threads does not cancel an in-flight mark-read or send.
  yield takeEvery(MARK_CHAT_CONVERSATION_READ, markChatConversationReadSaga);
  yield takeEvery(SEND_CHAT_MESSAGE, sendChatMessageSaga);
  yield takeLatest(CREATE_CHAT_DIRECT, createChatDirectSaga);
}
